/**
 * Assets du brain — construction des URLs et teintes.
 *
 * Fonctions pures : l'URL de base est passée en argument plutôt que lue dans
 * le store, pour rester testable sans contexte Pinia. C'est à l'appelant de
 * la fournir, via `assetsBase(useConfigStore().urls.assets)`.
 */

/** brain.js:59-61 — `${urls.assets}/img/` */
export function assetsBase(configAssetsUrl: string): string {
  return `${configAssetsUrl}/img/`
}

/**
 * Type de masque -> fichier. Transcrit de la cascade de `if` de
 * brain.js:1417-1478.
 */
const MASK_SHAPES: Record<string, string> = {
  Simple: "brain/masks/rectangle.png",
  Arrow: "brain/masks/mask-arrow.png",
  BigSquare: "brain/masks/transp-mask-big-square.png",
  Duo: "brain/masks/transp-mask-duo.png",
  BigCircle: "brain/masks/mask-circle.png",
  Halfcircle: "brain/masks/mask-half-circle.png",
  HalfCircle: "brain/masks/mask-half-circle.png",
  U: "brain/masks/mask-U.png",
  Flash: "brain/masks/mask-flash.png",
  TranspFlash: "brain/masks/transp-mask-flash.png",
  ThreeSquares: "brain/masks/mask-3-square.png",
  Diamond: "brain/masks/mask-losange.png",
  Snake: "brain/masks/mask-serpent.png",
  TwoHalfCircles: "brain/masks/two-half-circle.png",
  // `Rectangle` n'a pas d'image : ses coins sont rendus en border-radius
  // (cf. NINE_SLICE_RADIUS dans geometry.ts).
  Rectangle: "brain/masks/mask2.png",
}

/**
 * Teintes de brain.js. Les assets étant des silhouettes blanches, le `tint`
 * de Pixi se traduit exactement en `background-color` derrière un `mask-image`.
 */
export const BRAIN_COLORS = {
  /** Token et dropzone au repos — brain.js:963, 1076 */
  token: "#067c8a",
  /** Figure cible, en gris — brain.js:697 */
  figure: "#61686e",
  /** Fond de masque au repos — brain.js:1479 */
  maskIdle: "#f1f1f1",
  /** Fond de masque, fusion légale — brain.js:1645 */
  maskLegal: "#d6edef",
  /** Fond de masque, fusion illégale — brain.js:1686 */
  maskIllegal: "#ffc6c6",
  /** Dropzone en erreur — brain.js:1685 */
  dropzoneError: "#cb382e",
} as const

/**
 * Règle commune aux tokens et aux figures : un "neg" dans l'URL est retiré, et
 * le dossier numeric bascule en custom-figures.
 * (brain.js:937-942 et 1015-1020)
 */
function applyNegRule(url: string): string {
  return url.indexOf("neg") > -1
    ? url.replace("neg", "").replace("numeric", "custom-figures")
    : url
}

/** URL de la silhouette d'un masque. `null` si le type est inconnu. */
export function maskShapeUrl(base: string, type: string): string | null {
  const file = MASK_SHAPES[type]
  return file ? base + file : null
}

/**
 * URL d'un TOKEN.
 *
 * Attention : contrairement aux figures, il n'y a PAS de règle sur le "-"
 * initial ici. brain.js construit toujours le chemin sur le dossier du type
 * (createTest:656-662). Le token "-1c1" de type Numeric vit donc dans
 * /numeric et non dans /custom-figures — les deux fichiers existent et sont
 * différents, la confusion est silencieuse.
 */
export function tokenUrl(base: string, name: string, type: string): string {
  return applyNegRule(
    `${base}brain/materials/${type.toLowerCase()}/${name}.png`
  )
}

/**
 * URL d'une FIGURE.
 *
 * Ici, et seulement ici, un nom commençant par "-" bascule dans
 * /custom-figures (brain.js:686-696).
 */
export function figureUrl(base: string, name: string, type: string): string {
  const url =
    name[0] === "-"
      ? `${base}brain/materials/custom-figures/${name}.png`
      : `${base}brain/materials/${type.toLowerCase()}/${name}.png`
  return applyNegRule(url)
}

/** URL de l'image de dropzone vide. */
export function dropzoneUrl(base: string): string {
  return `${base}brain/dropzones/DropZone.png`
}

/**
 * Le doigt affiché pendant la démonstration du tutoriel (brain.js:86).
 *
 * Contrairement aux autres assets ce n'est PAS une silhouette blanche : Pixi
 * ne lui applique aucune teinte, il est rendu tel quel. Il ne passe donc pas
 * par `mask-image` mais par une simple image.
 */
export function fingerUrl(base: string): string {
  return `${base}brain/ui/hand-pointer.png`
}

/**
 * Identité d'un token, une fois le marqueur "neg" traité.
 *
 * brain.js:940 retire "neg" du NOM du token, pas seulement de son URL. Or
 * c'est ce nom épuré que la logique de fusion parse ensuite — le confondre
 * avec le nom brut donnerait des composantes fausses, donc un mauvais score.
 * `isNeg` conserve l'information, dont la fusion a besoin séparément.
 */
export function resolveTokenIdentity(rawName: string): {
  name: string
  isNeg: boolean
} {
  return rawName.indexOf("neg") > -1
    ? { name: rawName.replace("neg", ""), isNeg: true }
    : { name: rawName, isNeg: false }
}

/**
 * Nom du fichier de texture d'une dropzone remplie.
 *
 * Transcrit les trois branches de brain.js:1649-1678 : un résultat numeric
 * dont `n` vaut 0 se lit dans /numeric, s'il est négatif dans
 * /custom-figures, sinon on prend le dossier du type des tokens présents.
 */
export function mergedTextureUrl(
  base: string,
  tokensType: string,
  texture: string,
  numericValue: number | null
): string {
  if (tokensType === "numeric" && numericValue === 0) {
    return `${base}brain/materials/numeric/${texture}.png`
  }
  if (tokensType === "numeric" && numericValue !== null && numericValue < 0) {
    return `${base}brain/materials/custom-figures/${texture}.png`
  }
  return `${base}brain/materials/${tokensType}/${texture}.png`
}

/**
 * Silhouettes chargées, indexées par URL d'origine.
 * Les valeurs sont des URL `blob:` — voir `loadSilhouette`.
 */
const silhouetteCache = new Map<string, Promise<string | null>>()

/**
 * Récupère le fichier, avec une seconde tentative qui contourne le cache HTTP.
 *
 * Le cache peut contenir une réponse SANS en-tête CORS — laissée par une
 * balise `<img>` sans `crossorigin`, qui n'envoie pas d'en-tête `Origin` et à
 * qui S3 ne renvoie donc pas d'`Access-Control-Allow-Origin`. Toute requête
 * CORS ultérieure réutilise cette entrée et échoue, avec un laconique
 * « Failed to fetch ».
 *
 * `cache: "reload"` ignore l'entrée, refait la requête réseau et la remplace
 * par une réponse exploitable. Les appels suivants repassent par le cache
 * normalement.
 */
function fetchBlob(url: string): Promise<Blob | null> {
  const attempt = (init?: RequestInit) =>
    fetch(url, init).then((response) => (response.ok ? response.blob() : null))

  return attempt()
    .catch(() => null)
    .then((blob) => blob ?? attempt({ cache: "reload" }).catch(() => null))
}

/**
 * Charge une silhouette et renvoie une URL `blob:` utilisable en `mask-image`.
 *
 * POURQUOI CE DÉTOUR, ET POURQUOI IL EST REVENU
 *
 * Un `mask-image` CSS est récupéré en mode CORS. Servir les assets depuis S3
 * expose donc le portage à toute la fragilité qui va avec — et le symptôme
 * d'un échec est particulièrement traître : l'élément devient totalement
 * transparent, SANS erreur ni avertissement. Il reste présent, mesurable,
 * survolable — le curseur change encore — mais invisible.
 *
 * Le 24/08/2026 ce détour a été supprimé, sur la foi de `poc/mask-support/`
 * qui montrait les trois moteurs appliquant un masque cross-origin. Le constat
 * était juste EN ISOLATION et faux dans l'app : la page de test ne
 * reproduisait pas ses conditions — pas de préchargement, donc une seule
 * requête, en mode CORS. Deux échecs en conditions réelles ont suivi. Le
 * détour est donc rétabli.
 *
 * `fetch` passe le contrôle CORS normalement, et l'URL `blob:` produite est de
 * MÊME ORIGINE : plus aucune requête cross-origin ne subsiste au moment où le
 * masque est appliqué. C'est ce qui rend le rendu insensible au cache, aux
 * en-têtes et au navigateur.
 *
 * Renvoie `null` si le chargement échoue : un asset manquant doit se voir à
 * l'écran, pas faire échouer le rendu de tout l'item.
 */
export function loadSilhouette(url: string): Promise<string | null> {
  const cached = silhouetteCache.get(url)
  if (cached) return cached

  const promise = fetchBlob(url)
    .then((blob) => (blob ? URL.createObjectURL(blob) : null))
    .then((result) => {
      // Un échec ne doit PAS rester en cache : sinon un incident réseau
      // passager blanchit l'item pour toute la session, sans moyen de s'en
      // remettre. En l'oubliant, le prochain rendu retente.
      if (result === null) silhouetteCache.delete(url)
      return result
    })

  silhouetteCache.set(url, promise)
  return promise
}

/** Charge un lot de silhouettes. Les `null` sont ignorés. */
export function loadSilhouettes(
  urls: (string | null)[]
): Promise<Map<string, string>> {
  const unique = [...new Set(urls.filter((u): u is string => Boolean(u)))]

  return Promise.all(
    unique.map((url) =>
      loadSilhouette(url).then((blob) => [url, blob] as const)
    )
  ).then((entries) => {
    const map = new Map<string, string>()
    for (const [url, blob] of entries) {
      if (blob) map.set(url, blob)
    }
    return map
  })
}

/**
 * PAS DE RÉVOCATION DES `blob:`, ET C'EST DÉLIBÉRÉ
 *
 * Une fonction `revokeSilhouettes()` existait, appelée au démontage du brain
 * pour libérer les objets. Elle a causé des **écrans entièrement blancs** en
 * conditions réelles, et le mode d'échec mérite d'être connu :
 *
 * elle parcourait `silhouetteCache.values()` — un itérateur VIVANT — avec un
 * `await` à chaque tour. Pendant ces pauses, un remontage du composant
 * ajoutait de nouvelles entrées au cache, que l'itérateur ramassait et
 * révoquait aussitôt ; le `clear()` final effaçait le cache neuf par-dessus.
 * Le DOM se retrouvait avec des `mask-image` pointant des blobs révoqués.
 *
 * Le symptôme est particulièrement traître : l'URL reste une chaîne valide et
 * visible dans l'inspecteur, la ressource n'existe plus, l'élément devient
 * totalement transparent — sans la moindre erreur en console. En
 * développement, chaque rechargement à chaud déclenchait le cas.
 *
 * On aurait pu la corriger (copier les valeurs, vider avant d'attendre). On ne
 * la garde pas : elle libère quelques PNG de silhouettes, et met en risque
 * l'affichage entier de la feature. Le navigateur les libérera au déchargement
 * de la page.
 */

/**
 * Taille native d'une image, en px. `null` si elle ne charge pas.
 *
 * Sert au doigt du tutoriel : dans Pixi il est ENFANT du token, il hérite donc
 * de l'échelle de celui-ci — un token affiché en 100 px à partir d'une texture
 * de 175 px applique un facteur 100/175 à son doigt. Sans cette mesure, le
 * doigt serait rendu à sa taille native et paraîtrait deux fois trop gros.
 *
 * Mesuré le 24/08/2026 : hand-pointer.png 141x168, token abstrait 175x175,
 * DropZone.png 276x276.
 */
const naturalSizeCache = new Map<
  string,
  Promise<{ w: number; h: number } | null>
>()

export function naturalSize(
  url: string
): Promise<{ w: number; h: number } | null> {
  const cached = naturalSizeCache.get(url)
  if (cached) return cached

  const promise = new Promise<{ w: number; h: number } | null>((resolve) => {
    const img = new Image()
    // CORS explicite : ces URL sont aussi récupérées en `fetch` par
    // `loadSilhouette`, et mélanger les deux modes empoisonne le cache.
    img.crossOrigin = "anonymous"
    img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight })
    img.onerror = () => {
      // Pas de mise en cache d'un échec : un incident réseau passager ne doit
      // pas figer la taille pour toute la session.
      naturalSizeCache.delete(url)
      resolve(null)
    }
    img.src = url
  })

  naturalSizeCache.set(url, promise)
  return promise
}

/**
 * Le doigt du tutoriel, en `blob:`.
 *
 * Il est affiché tel quel dans une balise `<img>`, il n'a donc en principe
 * aucun besoin de CORS. Sauf que le bucket ne renvoie `Access-Control-Allow-
 * Origin` QUE si la requête porte un en-tête `Origin` (vérifié le 21/09/2026 :
 * même URL, une seule des deux réponses a l'en-tête), et il signale cette
 * dépendance par un `Vary: Origin` que le cache image de Safari ignore. Il
 * suffit alors qu'une réponse sans en-tête CORS soit mise en cache pour que
 * toute balise `crossorigin` pointant cette URL échoue — le doigt devient une
 * image cassée, et recharger la page n'y change rien tant que l'entrée vit.
 *
 * Le détour par `fetch` + `blob:` supprime le problème à la racine, exactement
 * comme pour les silhouettes : l'URL posée sur l'élément est de MÊME ORIGINE,
 * plus aucune requête cross-origin ne subsiste à l'affichage. On réutilise
 * `loadSilhouette` pour son cache et sa reprise `cache: "reload"`, même si le
 * doigt n'est pas une silhouette.
 */
export function loadFingerImage(base: string): Promise<string | null> {
  return loadSilhouette(fingerUrl(base))
}
